import React, { useCallback, useMemo, useState } from 'react';
import { BASE_EXCLUDE_COLOR_PALETTE, PROJECT_PROGRAM_COLOR_PALETTE, REGULAR_PROGRAM_COLOR_PALETTE } from '../constants/programColorPalettes';

export type ProgramColorPickModalType = 'regular' | 'dedicated' | 'baseExclude' | 'project';

const REGULAR_COLOR_PALETTE = [...REGULAR_PROGRAM_COLOR_PALETTE];

/** 전담 프로그램 색상표: 옅은 그레이 파스텔 */
const DEDICATED_COLOR_PALETTE = [
  '#e8d3ce',
  '#e3ddc7',
  '#cfe0d0',
  '#cad8e3',
  '#d9d0e3',
  '#e4d0d9',
  '#e0dcd3',
  '#d4d7dc',
  '#dddfd4',
];

function normalizeHex(hex: string): string {
  return hex.trim().toLowerCase();
}

function isHexInColorList(hex: string, list: string[]): boolean {
  const n = normalizeHex(hex);
  return list.some((c) => normalizeHex(c) === n);
}

export interface ProgramColorModalSubmitPayload {
  hex: string;
  label: string;
  mode: 'create' | 'edit';
  originalHex?: string;
}

export interface ProgramColorPickModalProps {
  type: ProgramColorPickModalType;
  existingColors: string[];
  mode?: 'create' | 'edit';
  initialHex?: string;
  initialLabel?: string;
  /** true면 팔레트 없이 커스텀 색상 선택 화면만 */
  customOnly?: boolean;
  showLabel?: boolean;
  onSubmit: (payload: ProgramColorModalSubmitPayload) => void;
  onClose: () => void;
  /** 수정 모드에서만: 등록 색상 삭제(등록 해제) */
  onDelete?: () => void;
}

const ProgramColorPickModal: React.FC<ProgramColorPickModalProps> = ({
  type,
  existingColors,
  mode = 'create',
  initialHex,
  initialLabel = '',
  customOnly = false,
  showLabel = true,
  onSubmit,
  onClose,
  onDelete,
}) => {
  const isEditMode = mode === 'edit';
  const canDelete = isEditMode && typeof onDelete === 'function' && Boolean((initialHex ?? '').trim());
  const [showCustomColorInModal, setShowCustomColorInModal] = useState(customOnly);
  const [selectedHex, setSelectedHex] = useState(initialHex?.trim() || '');
  const [customColorDraft, setCustomColorDraft] = useState(initialHex?.trim() || '#4ead75');
  const [labelDraft, setLabelDraft] = useState(initialLabel);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const palette =
    type === 'regular'
      ? REGULAR_COLOR_PALETTE
      : type === 'dedicated'
        ? DEDICATED_COLOR_PALETTE
        : type === 'project'
          ? [...PROJECT_PROGRAM_COLOR_PALETTE]
          : BASE_EXCLUDE_COLOR_PALETTE;

  React.useEffect(() => {
    if (customOnly || selectedHex.trim()) return;
    setSelectedHex(palette[0] ?? '#4ead75');
  }, [customOnly, palette, selectedHex]);

  const handleClose = useCallback(() => {
    if (!customOnly) setShowCustomColorInModal(false);
    setDeleteConfirmOpen(false);
    onClose();
  }, [customOnly, onClose]);

  const handleRequestDelete = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    onDelete?.();
  }, [onDelete]);

  const handleOpenCustomColor = useCallback(() => {
    setShowCustomColorInModal(true);
    setCustomColorDraft(selectedHex || '#4ead75');
  }, [selectedHex]);

  const handlePickPalette = (hex: string) => {
    setSelectedHex(hex);
    setShowCustomColorInModal(false);
  };

  const handleConfirmCustomColor = () => {
    setSelectedHex(customColorDraft);
    setShowCustomColorInModal(false);
  };

  const handleConfirm = () => {
    if (customOnly) {
      onSubmit({
        hex: customColorDraft,
        label: labelDraft.trim(),
        mode,
        originalHex: isEditMode ? initialHex : undefined,
      });
      return;
    }
    onSubmit({
      hex: selectedHex,
      label: labelDraft.trim(),
      mode,
      originalHex: isEditMode ? initialHex : undefined,
    });
    setShowCustomColorInModal(false);
  };

  const selectedColorAlreadyRegistered = useMemo(() => {
    const selected = normalizeHex(selectedHex);
    if (!selected) return false;
    const original = normalizeHex(initialHex ?? '');
    return existingColors.some((hex) => {
      const normalized = normalizeHex(hex);
      if (isEditMode && normalized === original) return false;
      return normalized === selected;
    });
  }, [selectedHex, existingColors, isEditMode, initialHex]);

  const customColorAlreadyRegistered = useMemo(() => {
    if (!showCustomColorInModal) return false;
    if (customOnly) return false;
    if (isEditMode && normalizeHex(customColorDraft) === normalizeHex(initialHex ?? '')) return false;
    return isHexInColorList(customColorDraft, existingColors);
  }, [showCustomColorInModal, customColorDraft, existingColors, isEditMode, initialHex, customOnly]);

  const title = customOnly
    ? '색상 선택'
    : type === 'regular'
      ? isEditMode
        ? '고정 프로그램 색상 수정'
        : '고정 프로그램 색상 선택'
      : type === 'dedicated'
        ? isEditMode
          ? '전담 프로그램 색상 수정'
          : '전담 프로그램 색상 선택'
        : type === 'project'
          ? isEditMode
            ? '프로젝트 색상 수정'
            : '프로젝트 색상 선택'
          : isEditMode
            ? '라벨링 색상 수정'
            : '라벨링 색상 선택';

  const paletteDesc =
    type === 'regular'
      ? '고정 프로그램 사전 색상(3개) 중 하나를 선택하세요.'
      : type === 'dedicated'
        ? '파스텔톤 색상 중 하나를 선택하세요.'
        : type === 'project'
          ? '프로젝트용 색상 중 하나를 선택하세요.'
          : '선명한 색상 중 하나를 선택하세요. (초록 계열 제외)';

  const paletteClass =
    type === 'regular'
      ? 'program-color-palette program-color-palette-3x1'
      : type === 'dedicated'
        ? 'program-color-palette program-color-palette-3x3'
        : 'program-color-palette program-color-palette-3x4';

  /** 수정 중인 색상이 사전 팔레트 밖의 등록(기타) 색일 때만 1개 표시 */
  const editingCustomColorHex = useMemo(() => {
    if (!isEditMode) return null;
    const hex = (initialHex ?? '').trim();
    if (!hex) return null;
    if (isHexInColorList(hex, [...palette])) return null;
    if (!isHexInColorList(hex, existingColors)) return null;
    return hex;
  }, [isEditMode, initialHex, palette, existingColors]);

  return (
    <div className="cp-modal-backdrop" onClick={handleClose}>
      <div
        className="cp-modal program-color-modal"
        data-tutorial-color-modal-type={type}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="program-color-modal-title-row">
          <h3>{title}</h3>
          {canDelete ? (
            <button
              type="button"
              className="program-color-modal-delete-btn"
              onClick={handleRequestDelete}
              aria-label="등록된 색상 삭제"
              title="색상 삭제"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/Trash.svg`}
                alt=""
                aria-hidden
                className="program-color-modal-delete-icon"
              />
            </button>
          ) : null}
        </div>
        {showLabel ? (
          <div className="program-color-modal-label-row">
            <label htmlFor="cp-program-color-label-input" className="program-color-modal-label-title">
              라벨 이름
            </label>
            <input
              id="cp-program-color-label-input"
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              className="program-color-modal-label-input"
              maxLength={40}
              placeholder="예: 체육관 행사"
              autoComplete="off"
            />
          </div>
        ) : null}
        {customOnly ? (
          <>
            <p className="page-desc">원하는 색상을 선택한 뒤 확인을 누르세요.</p>
            <div className="program-color-picker-inline">
              <input
                type="color"
                value={customColorDraft}
                onChange={(e) => setCustomColorDraft(e.target.value)}
                className="program-color-picker-input"
              />
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={handleClose}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={handleConfirm}>
                확인
              </button>
            </div>
          </>
        ) : showCustomColorInModal ? (
          <>
            <p className="page-desc">원하는 색상을 선택한 뒤 확인을 누르세요.</p>
            <div className="program-color-picker-inline">
              <input
                type="color"
                value={customColorDraft}
                onChange={(e) => setCustomColorDraft(e.target.value)}
                className="program-color-picker-input"
              />
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setShowCustomColorInModal(false)}>
                뒤로
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleConfirmCustomColor}
                disabled={customColorAlreadyRegistered}
              >
                확인
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="page-desc">{paletteDesc}</p>
            <div className={paletteClass}>
              {palette.map((hex) => {
                const taken =
                  isHexInColorList(hex, existingColors) &&
                  (!isEditMode || normalizeHex(hex) !== normalizeHex(initialHex ?? ''));
                return (
                  <button
                    key={hex}
                    type="button"
                    className={
                      'program-color-swatch program-color-swatch-btn' +
                      (normalizeHex(selectedHex) === normalizeHex(hex) ? ' is-selected' : '')
                    }
                    disabled={taken}
                    onClick={() => handlePickPalette(hex)}
                    title={taken ? `${hex} (이미 등록됨)` : hex}
                    aria-label={taken ? `색상 ${hex}, 이미 등록됨` : `색상 ${hex}`}
                  >
                    <span className="program-color-swatch-inner" style={{ backgroundColor: hex }} aria-hidden />
                  </button>
                );
              })}
            </div>
            <div className="program-color-modal-custom-row">
              <button
                type="button"
                className="cp-btn-secondary program-color-custom-btn program-color-custom-btn-block"
                onClick={handleOpenCustomColor}
              >
                기타 색상 선택
              </button>
            </div>
            {editingCustomColorHex ? (
              <div
                className="program-color-modal-custom-registered-palette"
                aria-label="수정 중인 기타 색상"
              >
                <button
                  type="button"
                  className={
                    'program-color-swatch program-color-swatch-btn' +
                    (normalizeHex(selectedHex) === normalizeHex(editingCustomColorHex) ? ' is-selected' : '')
                  }
                  onClick={() => handlePickPalette(editingCustomColorHex)}
                  title={editingCustomColorHex}
                  aria-label={`수정 중인 기타 색상 ${editingCustomColorHex}`}
                >
                  <span
                    className="program-color-swatch-inner"
                    style={{ backgroundColor: editingCustomColorHex }}
                    aria-hidden
                  />
                </button>
              </div>
            ) : null}
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={handleClose}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleConfirm}
                disabled={!selectedHex || selectedColorAlreadyRegistered}
              >
                {isEditMode ? '저장' : '확인'}
              </button>
            </div>
          </>
        )}
      </div>
      {deleteConfirmOpen ? (
        <div
          className="cp-modal-backdrop program-color-modal-delete-confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-program-color-delete-confirm-title"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteConfirmOpen(false);
          }}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="cp-program-color-delete-confirm-title">색상 삭제</h3>
            <p className="page-desc">선택한 색상을 삭제할까요?</p>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteConfirmOpen(false)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={handleConfirmDelete}>
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProgramColorPickModal;
